import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import {
  Badge,
  Button,
  LoadingState,
  Toast,
  IconArrowLeft,
  IconChart,
  IconClock,
  IconEye,
  IconStar,
  IconUsers,
} from "../ui";
import { usePageMetadata } from "../seo/usePageMetadata";

const pageLabels = {
  overview: "Menú del torneo",
  groups: "Grupos",
  matches: "Partidos",
  bracket: "Eliminatoria",
  games: "Juegos",
};

function MetricCard({ label, value, hint, icon }) {
  return (
    <article className="rounded-2xl border border-arena/10 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">{label}</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-ink">{Number(value || 0).toLocaleString("es-ES")}</p>
          {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
        </div>
        <span className="rounded-xl bg-arena/8 p-2.5 text-arena">{icon}</span>
      </div>
    </article>
  );
}

function BarList({ rows, labelForRow, emptyText }) {
  const max = Math.max(0, ...rows.map((row) => row.count ?? row.visits ?? 0));
  if (!rows.length || max === 0) {
    return <p className="py-8 text-center text-sm text-ink-muted">{emptyText}</p>;
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const value = row.count ?? row.visits ?? 0;
        return (
          <div key={row.label ?? row.hour}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="truncate font-semibold text-ink">{labelForRow(row)}</span>
              <span className="font-bold tabular-nums text-arena">{value.toLocaleString("es-ES")}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-arena/8">
              <div
                className="h-full rounded-full bg-gradient-to-r from-arena to-arena-bright"
                style={{ width: `${Math.max(3, (value / max) * 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TournamentAnalyticsPage() {
  const { tournamentId } = useParams();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  usePageMetadata({
    title: "Audiencia del torneo | Club Evolver",
    description: "Estadísticas privadas de audiencia del torneo.",
    canonicalPath: `/tournamentactions/${tournamentId}/audiencia`,
    robots: "noindex,nofollow",
  });

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${appConstants.API_URL}/api/tournaments/${tournamentId}/analytics/summary`,
        {
          params: {
            days,
            timezoneOffsetMinutes: new Date().getTimezoneOffset(),
          },
        }
      );
      setSummary(res.data);
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 403
          ? "Solo el organizador y los administradores pueden ver estas estadísticas."
          : "No se pudieron cargar las estadísticas de audiencia."
      );
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [days, tournamentId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const peakHour = useMemo(() => {
    const rows = summary?.hourly ?? [];
    if (!rows.length) return null;
    return rows.reduce((best, row) => (row.visits > best.visits ? row : best), rows[0]);
  }, [summary]);

  const recentDaily = useMemo(() => (summary?.daily ?? []).slice(-14), [summary]);
  const maxDailyVisits = Math.max(1, ...recentDaily.map((row) => row.visits));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
      <RouterLink
        to={`/tournamentactions/${tournamentId}`}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-arena"
      >
        <IconArrowLeft size={16} />
        Volver al torneo
      </RouterLink>

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Badge variant="gold">Privado para organizadores</Badge>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-4xl">Audiencia del torneo</h1>
          <p className="mt-2 text-ink-muted">
            {summary?.tournamentName || "Torneo"}: visitas, personas, horas de entrada y páginas más consultadas.
          </p>
        </div>
        <div className="flex gap-2" aria-label="Periodo de estadísticas">
          {[7, 30, 90].map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={days === option ? "primary" : "outline"}
              onClick={() => setDays(option)}
            >
              {option} días
            </Button>
          ))}
        </div>
      </div>

      {loading ? <LoadingState label="Calculando audiencia..." className="min-h-[35vh]" /> : null}
      {error && !loading ? <Toast kind="error">{error}</Toast> : null}

      {!loading && summary ? (
        <div className="space-y-8">
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Personas únicas"
              value={summary.periodUniqueVisitors}
              hint={`Últimos ${summary.periodDays} días`}
              icon={<IconUsers />}
            />
            <MetricCard
              label="Visitas"
              value={summary.periodVisits}
              hint={`${summary.visitsLast24Hours} en las últimas 24 h`}
              icon={<IconEye />}
            />
            <MetricCard
              label="Páginas vistas"
              value={summary.periodPageViews}
              hint={`${summary.averagePagesPerVisit} páginas por visita`}
              icon={<IconChart />}
            />
            <MetricCard
              label="Seguidores registrados"
              value={summary.registeredFollowers}
              hint={`${summary.returningVisitors} personas volvieron en el periodo`}
              icon={<IconStar />}
            />
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <article className="rounded-2xl border border-arena/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-bold text-ink">Entradas por día</h2>
                  <p className="text-sm text-ink-muted">Últimos 14 días del periodo seleccionado.</p>
                </div>
                <IconChart className="text-arena" />
              </div>
              <div className="flex h-56 items-end gap-2" aria-label="Gráfico de visitas diarias">
                {recentDaily.map((row) => (
                  <div key={row.date} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-2">
                    <span className="text-[10px] font-bold tabular-nums text-ink-muted">{row.visits || ""}</span>
                    <div
                      className="w-full min-w-[8px] rounded-t-lg bg-gradient-to-t from-arena to-arena-bright"
                      style={{ height: `${row.visits ? Math.max(8, (row.visits / maxDailyVisits) * 170) : 2}px` }}
                      title={`${row.date}: ${row.visits} visitas`}
                    />
                    <span className="hidden text-[10px] text-ink-muted sm:block">{row.date.slice(8)}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="rounded-2xl border border-arena/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-bold text-ink">Horas de entrada</h2>
                  <p className="text-sm text-ink-muted">
                    {peakHour?.visits
                      ? `Hora punta: ${String(peakHour.hour).padStart(2, "0")}:00`
                      : "Aún no hay una hora punta."}
                  </p>
                </div>
                <IconClock className="text-arena" />
              </div>
              <BarList
                rows={(summary.hourly ?? []).filter((row) => row.visits > 0)}
                labelForRow={(row) => `${String(row.hour).padStart(2, "0")}:00-${String((row.hour + 1) % 24).padStart(2, "0")}:00`}
                emptyText="Todavía no hay visitas en este periodo."
              />
            </article>
          </section>

          <section className="grid gap-6 md:grid-cols-2">
            <article className="rounded-2xl border border-arena/10 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="font-display text-xl font-bold text-ink">Páginas más consultadas</h2>
              <p className="mb-5 text-sm text-ink-muted">Qué contenido genera más interés.</p>
              <BarList
                rows={summary.pages ?? []}
                labelForRow={(row) => pageLabels[row.label] || row.label}
                emptyText="Todavía no hay páginas vistas."
              />
            </article>

            <article className="rounded-2xl border border-arena/10 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="font-display text-xl font-bold text-ink">Origen de las visitas</h2>
              <p className="mb-5 text-sm text-ink-muted">Enlaces directos y webs externas que enviaron público.</p>
              <BarList
                rows={summary.referrers ?? []}
                labelForRow={(row) => row.label}
                emptyText="Todavía no hay datos de procedencia."
              />
            </article>
          </section>

          <section className="rounded-2xl border border-arena/15 bg-arena/[0.04] px-5 py-4 text-sm text-ink-muted">
            Histórico desde que se activó la medición:{" "}
            <strong className="text-ink">{summary.allTimeUniqueVisitors.toLocaleString("es-ES")} personas</strong>,{" "}
            <strong className="text-ink">{summary.allTimeVisits.toLocaleString("es-ES")} visitas</strong> y{" "}
            <strong className="text-ink">{summary.allTimePageViews.toLocaleString("es-ES")} páginas vistas</strong>.
            No se guardan direcciones IP; visitantes y sesiones se identifican con códigos aleatorios del navegador.
          </section>
        </div>
      ) : null}
    </div>
  );
}
